import { forceCenter, forceSimulation } from "d3-force";
import { forceCluster } from "d3-force-cluster";
import { quadtree } from "d3-quadtree";
import { constant } from "lodash";
function SinRNG(seed) {
    return {
        seed: seed === undefined ? +new Date() + Math.random() : seed,
        random: function () {
            this.seed = Math.sin(this.seed) * 10000;
            return this.seed - Math.floor(this.seed);
        },
    };
}
// TODO: this is crying out for unit tests
export const computeNodePositions = (config) => {
    const maxRadius = config.maxRadius !== undefined ? config.maxRadius : 0.008;
    const gravity = config.gravity !== undefined ? config.gravity : 0.5;
    const clusterCount = 1;
    const clusters = new Array(clusterCount);
    const rng = SinRNG(config.seed || 1234);
    const random = rng.random.bind(rng);
    const inputNodes = [];
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    config.boxes.forEach((box) => {
        const index = Math.floor(random() * clusterCount);
        const radius = Math.sqrt(((index + 1) / clusterCount) * -Math.log(random())) * maxRadius;
        const inputNode = {
            cluster: index,
            radius: radius,
            uid: box.uid,
            width: box.size.x,
            height: box.size.y,
            x: box.schematicPosition?.x * 0.0001,
            y: box.schematicPosition?.y * 0.0001,
            pcbPosition: box.pcbPosition,
            margin: box.margin,
        };
        if (!clusters[index] || radius > clusters[index].radius) {
            clusters[index] = inputNode;
        }
        inputNodes.push(inputNode);
    });
    const result = inputNodes.map((inputNode) => {
        const rect = {
            uid: inputNode.uid,
            size: {
                x: inputNode.width + inputNode.margin,
                y: inputNode.height + inputNode.margin,
            },
            position: { x: 0, y: 0 },
        };
        return rect;
    });
    const simulation = forceSimulation();
    simulation.stop();
    simulation.force("center", forceCenter(0, 0)); // keep entire simulation balanced around screen center
    if (gravity > 0 && gravity <= 1) {
        simulation.force(
        // cluster by section
        "cluster", forceCluster()
            .centers((inputNode) => {
            return clusters[inputNode.cluster];
        })
            .strength(gravity));
    }
    // apply collision with padding
    simulation.force("collision", rectCollide().size((inputNode) => {
        return [
            inputNode.width + inputNode.margin + inputNode.margin,
            inputNode.height + inputNode.margin + inputNode.margin,
        ];
    }));
    //--- box collision
    simulation.on("tick", layoutTick).nodes(inputNodes);
    function layoutTick() {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        inputNodes.forEach((inputNode, index) => {
            if (result) {
                if (inputNode.pcbPosition) {
                    if (!inputNode.pcbPosition.autoX)
                        inputNode.x = inputNode.pcbPosition.x;
                    if (!inputNode.pcbPosition.autoY)
                        inputNode.y = inputNode.pcbPosition.y;
                }
                const node = result[index];
                if (node) {
                    node.size.x = inputNode.width;
                    node.size.y = inputNode.height;
                    node.position.x = inputNode.x;
                    node.position.y = inputNode.y;
                }
            }
        });
    }
    const startIterations = config.startIterations || 1000;
    for (let i = 0; i < startIterations; i++)
        simulation.tick();
    layoutTick();
    return result;
};
// TODO: fix the typing here
function rectCollide() {
    let nodes, sizes, masses, size;
    let strength = 1;
    let iterations = 1;
    function force() {
        let node, size, mass, xi, yi;
        let i = -1;
        while (++i < iterations) {
            iterate();
        }
        function iterate() {
            let j = -1;
            const tree = quadtree(nodes, xCenter, yCenter).visitAfter(prepare);
            while (++j < nodes.length) {
                node = nodes[j];
                size = sizes[j];
                mass = masses[j];
                xi = xCenter(node);
                yi = yCenter(node);
                tree.visit(apply);
            }
        }
        function apply(quad, x0, y0, x1, y1) {
            const data = quad.data;
            const xSize = (size[0] + quad.size[0]) / 2;
            const ySize = (size[1] + quad.size[1]) / 2;
            if (data) {
                if (data.index <= node.index) {
                    return;
                }
                let x = xi - xCenter(data);
                let y = yi - yCenter(data);
                const xd = Math.abs(x) - xSize;
                const yd = Math.abs(y) - ySize;
                if (xd < 0 && yd < 0) {
                    const l = Math.sqrt(x * x + y * y);
                    const mDenom = masses[data.index] + mass;
                    if (l !== 0 && mDenom !== 0) {
                        const m = masses[data.index] / mDenom;
                        if (Math.abs(xd) < Math.abs(yd)) {
                            node.vx -= (x *= (xd / l) * strength) * m;
                            data.vx += x * (1 - m);
                        }
                        else {
                            node.vy -= (y *= (yd / l) * strength) * m;
                            data.vy += y * (1 - m);
                        }
                    }
                }
            }
            return x0 > xi + xSize || y0 > yi + ySize || x1 < xi - xSize || y1 < yi - ySize;
        }
        function prepare(quad) {
            if (quad.data) {
                quad.size = sizes[quad.data.index];
            }
            else {
                quad.size = [0, 0];
                let i = -1;
                while (++i < 4) {
                    if (quad[i] && quad[i].size) {
                        quad.size[0] = Math.max(quad.size[0], quad[i].size[0]);
                        quad.size[1] = Math.max(quad.size[1], quad[i].size[1]);
                    }
                }
            }
        }
    }
    function xCenter(d) {
        return d.x + d.vx;
    }
    function yCenter(d) {
        return d.y + d.vy;
    }
    force.initialize = function (_) {
        sizes = (nodes = _).map(size);
        masses = sizes.map((d) => {
            return d[0] * d[1];
        });
    };
    force.size = function (_) {
        return arguments.length ? ((size = typeof _ === "function" ? _ : constant(_)), force) : size;
    };
    force.strength = function (_) {
        return arguments.length ? ((strength = +_), force) : strength;
    };
    force.iterations = function (_) {
        return arguments.length ? ((iterations = +_), force) : iterations;
    };
    return force;
}
//# sourceMappingURL=PcbLayoutAutoInitialPositioning.js.map